/**
 * Five-view / legacy eight-view angle set — server-side source of truth (V5-a).
 *
 * Kill switch: STRONOM_FIVE_VIEW_SET (default ON; set to 'false' for legacy 8-view).
 * Pattern matches STRONOM_* flags that default ON when unset (C-3c).
 */

export type AngleViewEntry = {
  readonly kind: string;
  readonly name: string;
  readonly label: string;
  readonly generated: boolean;
};

/** Canonical validation / required-output order (legacy CANONICAL_ANGLE_KINDS). */
export const LEGACY_EIGHT = [
  'image:angle-lateral',
  'image:angle-medial',
  'image:angle-front',
  'image:angle-back',
  'image:angle-top',
  'image:angle-bottom',
  'image:angle-3q-front',
  'image:angle-3q-back',
] as const;

/** Legacy generation loop order (process-jobs angleKinds, flag OFF). */
const LEGACY_EIGHT_GENERATION_ORDER = [
  'image:angle-front',
  'image:angle-back',
  'image:angle-medial',
  'image:angle-lateral',
  'image:angle-3q-front',
  'image:angle-3q-back',
  'image:angle-top',
  'image:angle-bottom',
] as const;

/** Five-view display order (Outside first). DB kind strings are frozen. */
export const FIVE_VIEW_SET = [
  { kind: 'image:angle-lateral', name: 'lateral', label: 'Outside', generated: false },
  { kind: 'image:angle-medial', name: 'medial', label: 'Inside', generated: true },
  { kind: 'image:angle-top', name: 'top', label: 'Top', generated: true },
  { kind: 'image:angle-bottom', name: 'bottom', label: 'Outsole', generated: true },
  { kind: 'image:angle-3q-front', name: '3q-front', label: 'Front Quarter', generated: true },
] as const satisfies readonly AngleViewEntry[];

const FIVE_VIEW_GENERATION_ORDER = [
  'image:angle-medial',
  'image:angle-top',
  'image:angle-bottom',
  'image:angle-3q-front',
] as const;

export function isFiveViewSetEnabled(): boolean {
  return process.env.STRONOM_FIVE_VIEW_SET !== 'false';
}

/** Required job_outputs kinds for the active view set (5 or 8). */
export function requiredViewKinds(): readonly string[] {
  return isFiveViewSetEnabled()
    ? FIVE_VIEW_SET.map((v) => v.kind)
    : LEGACY_EIGHT;
}

/** Kinds the generation loop should NB2-generate (4 or 8). */
export function generatedViewKinds(): readonly string[] {
  return isFiveViewSetEnabled()
    ? [...FIVE_VIEW_GENERATION_ORDER]
    : [...LEGACY_EIGHT_GENERATION_ORDER];
}

/** Alias for requiredViewKinds — active contract for loaders and gates. */
export function activeViewKinds(): readonly string[] {
  return requiredViewKinds();
}

export type AngleReference = { kind: string; url: string };

export type AngleReferenceGateResult =
  | { ok: true; references: AngleReference[]; missingKinds?: undefined }
  | { ok: false; references: AngleReference[]; missingKinds: string[] };

/**
 * Evaluate whether all required angle kinds are present (3D gate / loader logic).
 * Superset passes: legacy 8-row jobs satisfy a 5-kind requirement when flag ON.
 */
export function evaluateRequiredAngleOutputs(
  outputsByKind: Record<string, string | null | undefined>,
  requiredKinds: readonly string[] = requiredViewKinds(),
): AngleReferenceGateResult {
  const references: AngleReference[] = [];
  const missingKinds: string[] = [];

  for (const kind of requiredKinds) {
    const url = outputsByKind[kind];
    if (url && typeof url === 'string' && url.trim().length > 0) {
      references.push({ kind, url: url.trim() });
    } else {
      missingKinds.push(kind);
    }
  }

  if (missingKinds.length > 0) {
    return { ok: false, references, missingKinds };
  }

  return { ok: true, references };
}

/** Master-reuse alias row for image:angle-lateral (five-view only). */
export function buildMasterAliasOutput(args: {
  parentSideJobId: string;
  sideOutputUrl: string;
  baseMeta: Record<string, unknown>;
}): { kind: string; url: string; meta: Record<string, unknown> } | null {
  if (!isFiveViewSetEnabled()) {
    return null;
  }

  return {
    kind: 'image:angle-lateral',
    url: args.sideOutputUrl,
    meta: {
      ...args.baseMeta,
      angle_name: 'lateral',
      source: 'master_reuse',
      parent_side_job: args.parentSideJobId,
    },
  };
}
